import { merge, cloneDeep } from 'lodash';
import { notification } from 'antd';
import ListingTypes from '../action-types/listing';
import ListingMediaTypes from '../action-types/listingMedia';
import { setImagesHidden } from '../actions/listing.helpers';
import {
  handleUpdateSingleParent,
  handleUpdateParent,
  setListingParents,
} from './listingParents';
import { setPuppyInventoryStatus } from './listingPuppy';

const initialState = {
  loading: true,
  error: null,
  data: null,
  loadingTransition: false,
  loadingHeaderSearch: false,
  isReadOnly: false,
  errorHeaserSearch: null,
  listsHeaderSearch: null,
  changedStatus: null,
  queryByUser: null,
  lists: null,
  userId: null,
  changedWorkFlowStatus: null,
  recipients: null,
  loadingPriceCalc: false,
};

const puppyMediaUpdate = (state, { payload: media }) => {
  const newState = JSON.parse(JSON.stringify(state));
  newState.data.puppy.media = setImagesHidden(media);
  return newState;
};

const addNewMediaItem = (state, { payload: item }) => {
  const newState = JSON.parse(JSON.stringify(state));
  newState.data.puppy.media.push(item);
  return newState;
};

const createMessageSuccess = (customMessage) => {
  notification.success({
    message: customMessage || 'Image edit completed',
  });
};

const createMessageError = () => {
  notification.error({
    message: 'Error editing image',
  });
};

const setCheckMediaToEdit = (state, { payload: { id, checked } }) => {
  const { media } = state.data.puppy;

  const updatedMedia = media.map((m) => {
    if (m.id === id) {
      return {
        ...m,
        checked,
      };
    }

    if (m.pendingEdit?.id === id) {
      return {
        ...m,
        pendingEdit: {
          ...m.pendingEdit,
          checked,
        },
        checked,
      };
    }

    return m;
  });

  const newState = cloneDeep(state);
  newState.data.puppy.media = updatedMedia;
  return newState;
};

const updatePuppyMediaImageAfterEdit = (
  state,
  { payload: { media, imageId } }
) => {
  const { media: puppyMedia } = state.data.puppy;

  const updatedMedia = puppyMedia.map((item) => {
    if (item.id === imageId) {
      return {
        ...media,
        visibilityId: item.visibilityId,
        viewOrder: item.viewOrder,
        pendingEdit: media.pendingEdit
          ? {
              ...media.pendingEdit,
              visibilityId: item.visibilityId,
            }
          : null,
      };
    }

    return item;
  });

  createMessageSuccess(`Image ${imageId} successfully edited`);

  const newState = cloneDeep(state);
  newState.data.puppy.media = updatedMedia;
  return newState;
};

const updateParentMediaImageAfterEdit = (
  state,
  { payload: { media, imageId, parentType } }
) => {
  const { media: parentMedia } = state.data.parents[parentType];
  const updatedMedia = parentMedia.map((item) => {
    if (item.id === imageId) {
      return {
        ...media,
      };
    }

    return item;
  });

  createMessageSuccess();

  const newState = cloneDeep(state);
  newState.data.parents[parentType].media = updatedMedia;
  return newState;
};

const reorderPuppyMedia = (state, { payload: media }) => {
  return puppyMediaUpdate(state, { payload: media });
};

const errorMediaImageAfterEdit = (state) => {
  createMessageError();
  return state;
};

function setLockedByUserId(action, state) {
  const { lockedByUserId } = action;
  const changeInState = {
    data: { lockedByUserId },
  };
  return merge({}, state, changeInState);
}

function setIsReadOnly(action, state) {
  const { isReadOnly } = action;
  return {
    ...state,
    isReadOnly,
    data: {
      ...state.data,
      isReadOnly,
    },
  };
}

function setWorkflowStatusId(action, state) {
  const { workflowStatusId } = action;
  const changeInState = {
    data: {
      workflowStatusId,
      workflowEntity: {
        currentWorkflowStatusId: workflowStatusId,
      },
    },
  };
  return merge({}, state, changeInState);
}

function setListingStatusReason(action, state) {
  const { listingStatusReason } = action;
  const newState = { ...state };
  newState.data.listingStatusReason = listingStatusReason;
  return newState;
}

const updatePuppyVideoVisibility = (
  state,
  { payload: { videoId, visibility } }
) => {
  const newState = cloneDeep(state);
  const { media: puppyMedia } = state.data.puppy;

  const updatedMedia = puppyMedia.map((item) => {
    if (item.id === videoId) {
      return {
        ...item,
        visibilityId: visibility,
      };
    }

    return item;
  });

  newState.data.puppy.media = updatedMedia;

  return newState;
};

const listingReducer = (state = initialState, action) => {
  switch (action.type) {
    case ListingTypes.FETCH_LISTING_REQUEST: {
      return {
        ...state,
        loading: true,
        data: null,
        error: null,
      };
    }
    case ListingTypes.FETCH_LISTING_SUCCESS: {
      const { data } = action;
      return {
        ...state,
        data,
        loading: false,
        error: null,
      };
    }
    case ListingTypes.FETCH_LISTING_ERROR: {
      const { error } = action;
      return {
        ...state,
        error,
        loading: false,
      };
    }
    case ListingTypes.RESET_LISTING: {
      return initialState;
    }
    case ListingTypes.FETCH_ALL_LISTS_REQUEST: {
      return {
        ...state,
        loadingLists: true,
      };
    }
    case ListingTypes.FETCH_ALL_LISTS_SUCCESS: {
      const { lists } = action;
      return {
        ...state,
        lists,
        loadingLists: false,
      };
    }
    case ListingTypes.FETCH_ALL_LISTS_ERROR: {
      const { error } = action;
      return {
        ...state,
        error,
        loadingLists: false,
      };
    }
    case ListingTypes.FETCH_HEADER_SEARCH_REQUEST: {
      return {
        ...state,
        loadingHeaderSearch: true,
      };
    }
    case ListingTypes.FETCH_HEADER_SEARCH_SUCCESS: {
      const { listsHeaderSearch } = action;
      return {
        ...state,
        listsHeaderSearch,
        loadingHeaderSearch: false,
      };
    }
    case ListingTypes.FETCH_HEADER_SEARCH_ERROR: {
      const { error } = action;
      return {
        ...state,
        errorHeaserSearch: error,
        loadingHeaderSearch: false,
      };
    }
    case ListingTypes.RESET_HEADER_SEARCH: {
      return {
        ...state,
        loadingHeaderSearch: false,
        listsHeaderSearch: null,
        errorHeaserSearch: null,
      };
    }
    case ListingTypes.SET_LISTING_LOADING: {
      const { loading } = action;
      return {
        ...state,
        loading,
      };
    }
    case ListingTypes.SET_LISTING_LOADING_TRANSITION: {
      const { loadingTransition } = action;
      return {
        ...state,
        loadingTransition,
      };
    }
    case ListingTypes.CHANGE_LISTING_STATUS_SUCCESS: {
      const { changedStatus } = action;
      return {
        ...state,
        loading: false,
        changedStatus,
      };
    }
    case ListingTypes.CHANGE_LISTING_STATUS_ERROR: {
      const { error } = action;
      return {
        ...state,
        error,
      };
    }
    case ListingTypes.RESET_LISTING_STATUS_CHANGED: {
      return {
        ...state,
        changedStatus: null,
      };
    }
    case ListingTypes.FETCH_LISTING_IDS_BY_USER_REQUEST: {
      return {
        ...state,
        loading: true,
        data: null,
      };
    }
    case ListingTypes.FETCH_LISTING_IDS_BY_USER_SUCCESS: {
      const { queryByUser, userId } = action;
      return {
        ...state,
        loading: false,
        queryByUser,
        userId,
      };
    }
    case ListingTypes.FETCH_LISTING_IDS_BY_USER_ERROR: {
      const { error } = action;
      return {
        ...state,
        loading: false,
        error,
      };
    }
    case ListingTypes.POSTPONE_LISTING_REQUEST: {
      return {
        ...state,
        loading: true,
        error: null,
      };
    }
    case ListingTypes.POSTPONE_LISTING_SUCCESS: {
      const { id } = action;
      return {
        ...state,
        loading: false,
        changedWorkFlowStatus: id,
        error: null,
      };
    }
    case ListingTypes.POSTPONE_LISTING_ERROR: {
      const { error } = action;
      return {
        ...state,
        loading: false,
        error,
      };
    }
    case ListingTypes.RESET_WORKFLOW_STATUS_CHANGED: {
      return {
        ...state,
        changedWorkFlowStatus: null,
      };
    }
    case ListingTypes.RESET_LISTING_ERROR: {
      return {
        ...state,
        error: null,
      };
    }
    case ListingTypes.FETCH_REASSIGN_RECIPIENTS_SUCCESS: {
      const { recipients } = action;
      return {
        ...state,
        loading: true,
        recipients,
      };
    }

    case ListingTypes.REASSIGN_LISTING_REQUEST: {
      return {
        ...state,
        error: null,
        loading: true,
      };
    }

    case ListingTypes.REASSIGN_LISTING_SUCCESS: {
      return {
        ...state,
        loading: false,
        wasReassigned: true,
      };
    }
    case ListingTypes.REASSIGN_LISTING_ERROR: {
      const { error } = action;
      return {
        ...state,
        error,
        loading: false,
        wasReassigned: false,
      };
    }
    case ListingTypes.SET_LOADING_PRICE_CALCULATION: {
      const { loading } = action;
      return {
        ...state,
        loadingPriceCalc: loading,
      };
    }
    case ListingTypes.SET_WORKFLOW_STATUS_ID: {
      return setWorkflowStatusId(action, state);
    }
    case ListingTypes.SET_LOCKED_BY_USER_ID: {
      return setLockedByUserId(action, state);
    }
    case ListingTypes.SET_IS_READ_ONLY: {
      return setIsReadOnly(action, state);
    }
    case ListingTypes.SET_PUPPY_INVENTORY_STATUS: {
      return setPuppyInventoryStatus(action, state);
    }
    case ListingTypes.SET_LISTING_STATUS_REASON: {
      return setListingStatusReason(action, state);
    }
    case ListingMediaTypes.PUPPY_MEDIA_UPDATE: {
      return puppyMediaUpdate(state, action);
    }
    case ListingMediaTypes.ADD_NEW_MEDIA_ITEM: {
      return addNewMediaItem(state, action);
    }
    case ListingMediaTypes.UPDATE_PUPPY_MEDIA_IMAGE_AFTER_EDIT: {
      return updatePuppyMediaImageAfterEdit(state, action);
    }
    case ListingMediaTypes.UPDATE_PARENT_MEDIA_IMAGE_AFTER_EDIT: {
      return updateParentMediaImageAfterEdit(state, action);
    }
    case ListingMediaTypes.ERROR_MEDIA_IMAGE_AFTER_EDIT: {
      return errorMediaImageAfterEdit(state);
    }
    case ListingTypes.REORDER_PUPPY_MEDIA: {
      return reorderPuppyMedia(state, action);
    }
    case ListingTypes.UPDATE_PARENT: {
      return handleUpdateParent(state, action);
    }
    case ListingTypes.SET_LISTING_PARENTS: {
      return setListingParents(action, state);
    }
    case ListingTypes.UPDATE_SINGLE_PARENT: {
      return handleUpdateSingleParent(state, action);
    }
    case ListingMediaTypes.UPDATE_PUPPY_VIDEO_ViSIBILITY: {
      return updatePuppyVideoVisibility(state, action);
    }
    case ListingTypes.CHECK_MEDIA_TO_EDIT: {
      return setCheckMediaToEdit(state, action);
    }
    default:
      return state;
  }
};

export default listingReducer;
